// client/src/components/pages/LexiStar/Dashboard/periodLogic.ts
//
// Period arithmetic for the LexiStar numbers screens (ST-00188 M4-D; LS-09 §3
// "time range control": presets plus custom). Periods are inclusive calendar
// days as 'YYYY-MM-DD' strings — the operation's own (SAST) days, which is what
// the KPI API's `from` / `to` take and what the period aggregates are keyed by.
// Everything lives in the URL (?from&to&scope) so a view can be shared (LS-09
// §1 principle 7).

import { toApiDateString } from '../../../../utils/dateUtils';

export type Grain = 'day' | 'week' | 'month';
export type PresetId = '7d' | '30d' | 'mtd' | 'last_month';

export interface Period {
  /** First day, inclusive, YYYY-MM-DD. */
  from: string;
  /** Last day, inclusive, YYYY-MM-DD. */
  to: string;
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

export const dayString = (d: Date): string => toApiDateString(d);

/** [year, month (1-12), day] of a YYYY-MM-DD string (NaN parts when malformed). */
function ymd(s: string): [number, number, number] {
  const [y = NaN, m = NaN, d = NaN] = s.slice(0, 10).split('-').map(Number);
  return [y, m, d];
}

/** Local-midnight Date for a YYYY-MM-DD string (the shared pickers work in local dates). */
export function parseDay(s: string): Date {
  const [y, m, d] = ymd(s);
  return new Date(y, m - 1, d);
}

export const isDay = (s: string | null | undefined): s is string =>
  typeof s === 'string' && DAY_RE.test(s) && !Number.isNaN(parseDay(s).getTime());

export const todayString = (): string => dayString(new Date());

export function addDays(s: string, n: number): string {
  const d = parseDay(s);
  return dayString(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n));
}

const utcOf = (s: string): number => {
  const [y, m, d] = ymd(s);
  return Date.UTC(y, m - 1, d);
};

/** Number of days in the period, both ends included. */
export const spanDays = (p: Period): number => Math.round((utcOf(p.to) - utcOf(p.from)) / DAY_MS) + 1;

export const PRESETS: { id: PresetId; label: string; shortLabel: string }[] = [
  { id: '7d', label: 'Last 7 days', shortLabel: '7 d' },
  { id: '30d', label: 'Last 30 days', shortLabel: '30 d' },
  { id: 'mtd', label: 'This month', shortLabel: 'Month' },
  { id: 'last_month', label: 'Last month', shortLabel: 'Prev.' },
];

export const DEFAULT_PRESET: PresetId = '30d';

export function presetPeriod(id: PresetId, today: string = todayString()): Period {
  const t = parseDay(today);
  switch (id) {
    case '7d':
      return { from: addDays(today, -6), to: today };
    case 'mtd':
      return { from: dayString(new Date(t.getFullYear(), t.getMonth(), 1)), to: today };
    case 'last_month':
      return {
        from: dayString(new Date(t.getFullYear(), t.getMonth() - 1, 1)),
        to: dayString(new Date(t.getFullYear(), t.getMonth(), 0)),
      };
    case '30d':
    default:
      return { from: addDays(today, -29), to: today };
  }
}

/** The preset a period corresponds to today, or null for a custom range. */
export function matchPreset(p: Period, today: string = todayString()): PresetId | null {
  const hit = PRESETS.find(({ id }) => {
    const q = presetPeriod(id, today);
    return q.from === p.from && q.to === p.to;
  });
  return hit ? hit.id : null;
}

/** The comparison period: the same number of days immediately before. */
export function previousPeriod(p: Period): Period {
  const n = spanDays(p);
  return { from: addDays(p.from, -n), to: addDays(p.from, -1) };
}

/** Sparkline / series grain for a period: daily up to ~6 weeks, weekly to ~6 months. */
export function grainFor(p: Period): Grain {
  const n = spanDays(p);
  if (n <= 45) return 'day';
  if (n <= 200) return 'week';
  return 'month';
}

/** ?from&to → Period (defaults to the default preset; a reversed range is swapped). */
export function readPeriod(params: URLSearchParams, today: string = todayString()): Period {
  const from = params.get('from');
  const to = params.get('to');
  if (isDay(from) && isDay(to)) return from <= to ? { from, to } : { from: to, to: from };
  return presetPeriod(DEFAULT_PRESET, today);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const monthName = (m: number): string => MONTHS[m - 1] ?? '';

/** "3 Sep 2026" */
export function dayLabel(s: string): string {
  const [y, m, d] = ymd(s);
  return `${d} ${monthName(m)} ${y}`;
}

/** "1 – 24 Sep 2026", "28 Aug – 3 Sep 2026", "5 Dec 2025 – 4 Jan 2026", "3 Sep 2026". */
export function periodLabel(p: Period): string {
  if (p.from === p.to) return dayLabel(p.from);
  const [fy, fm, fd] = ymd(p.from);
  const [ty, tm, td] = ymd(p.to);
  if (fy !== ty) return `${dayLabel(p.from)} – ${dayLabel(p.to)}`;
  if (fm !== tm) return `${fd} ${monthName(fm)} – ${td} ${monthName(tm)} ${ty}`;
  return `${fd} – ${td} ${monthName(tm)} ${ty}`;
}

/** Short tick for a series point: "3 Sep" (day / week) or "Sep 26" (month). */
export function periodTick(periodStart: string, grain: Grain): string {
  const [y, m, d] = ymd(periodStart);
  if (grain === 'month') return `${monthName(m)} ${String(y).slice(2)}`;
  return `${d} ${monthName(m)}`;
}
